<script setup lang="ts">
import { computed, ref, type Component } from "vue";
import { onClickOutside, useElementBounding, useWindowSize } from "@vueuse/core";
import { ChevronRight } from "@lucide/vue";
import { openFlyoutId } from "@/features/PageNavItem/flyoutState";

const props = withDefaults(
  defineProps<{
    label: string;
    icon?: string;
    iconComponent?: Component;
    linkId?: string;
    paddingClass?: string;
    isExpanded?: boolean;
    disabled?: boolean;
    disabledReason?: string;
    // Highlights the row like an active nav link. For flyouts whose panel can navigate somewhere
    // (Reports → the LCA report page), so the sidebar still shows where you are once it closes.
    active?: boolean;
  }>(),
  { paddingClass: "px-2 py-1.5", isExpanded: true, disabled: false, active: false }
);

const flyoutId = props.linkId || `sidebar-flyout-${props.label}`;
const ignoreFlyoutId = `ignore-${flyoutId}`;
const buttonRef = ref<HTMLElement | null>(null);
const flyoutRef = ref<HTMLElement | null>(null);

const showFlyout = computed(() => openFlyoutId.value === flyoutId);

const toggleFlyout = () => {
  if (props.disabled) return;
  openFlyoutId.value = showFlyout.value ? null : flyoutId;
};

onClickOutside(
  flyoutRef,
  () => {
    if (showFlyout.value) openFlyoutId.value = null;
  },
  { ignore: [buttonRef, `#${ignoreFlyoutId}`] }
);

const { top: buttonTop, bottom: buttonBottom, right: buttonRight } = useElementBounding(buttonRef);
const { height: windowHeight } = useWindowSize();

const FLYOUT_MAX_HEIGHT = 440;
const VIEWPORT_PADDING = 8;

const flyoutStyle = computed(() => {
  const left = `${buttonRight.value + 8}px`;
  const spaceBelow = windowHeight.value - buttonTop.value - VIEWPORT_PADDING;

  if (spaceBelow >= FLYOUT_MAX_HEIGHT) {
    return { position: "fixed" as const, top: `${buttonTop.value}px`, left };
  }

  return {
    position: "fixed" as const,
    bottom: `${Math.max(VIEWPORT_PADDING, windowHeight.value - buttonBottom.value)}px`,
    left,
  };
});
</script>

<template>
  <div class="relative">
    <!-- aria-disabled rather than the native attribute: Chrome fires no mouse events on a natively
         disabled control, so the tooltip carrying `disabledReason` would never show. It used to work only
         because the old wrapper element received the hover. `toggleFlyout` already returns early when
         disabled, so the click needs no extra guard. -->
    <button
      :id="linkId"
      ref="buttonRef"
      v-tooltip="{
        text: disabled ? disabledReason || label : label,
        variant: 'label',
        placement: 'right',
        disabled: disabled ? false : isExpanded || showFlyout,
      }"
      type="button"
      :aria-disabled="disabled || undefined"
      :aria-label="disabled && disabledReason ? `${label} — ${disabledReason}` : label"
      :aria-haspopup="true"
      :aria-expanded="showFlyout"
      :aria-current="active ? 'page' : undefined"
      :class="[
        'gap-px-12 duration-fast ease-standard rounded-8 flex h-fit w-full items-center overflow-hidden whitespace-nowrap transition-colors',
        paddingClass,
        disabled
          ? 'text-type-muted interactive-disabled'
          : showFlyout || active
            ? 'focus-ring interactive-active text-type-default ring-app-blue-500 bg-app-blue-200/20 ring-1'
            : 'focus-ring interactive-active text-type-muted hover:text-type-default hover:bg-app-blue-200/20',
      ]"
      @click="toggleFlyout">
      <component
        :is="iconComponent"
        v-if="iconComponent"
        :stroke-width="1.5"
        color="var(--color-app-blue-800)"
        class="icon-md shrink-0" />
      <font-awesome-icon
        v-else-if="icon"
        :icon="icon"
        class="icon-md shrink-0" />
      <span
        :class="[
          'text-app-blue-900 t-small-bold duration-fast ease-standard flex-1 text-left transition-opacity will-change-[opacity]',
          isExpanded ? 'opacity-100' : 'opacity-0',
        ]"
        aria-hidden="true">
        {{ label }}
      </span>
      <ChevronRight
        v-if="isExpanded"
        :stroke-width="1.5"
        class="icon-sm text-app-blue-800/50 duration-fast ease-standard shrink-0 transition-transform"
        :class="{ 'translate-x-px-2': showFlyout }"
        aria-hidden="true" />
    </button>

    <Teleport to="body">
      <Transition name="fade-right">
        <div
          v-if="showFlyout"
          :id="ignoreFlyoutId"
          ref="flyoutRef"
          role="menu"
          :aria-label="`${label} menu`"
          :style="flyoutStyle"
          class="rounded-8 z-dropdown bg-slate-0 flex max-h-[440px] w-auto min-w-[200px] flex-col overflow-y-auto border border-slate-200 p-2 sm:max-w-[520px]">
          <slot name="flyout" />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
